import utils from '@/utils/index';
import { FormLengthValidatorFields, FormPatternValidatorFields } from '@/types/form/validator';
import { ScItem } from '@/types/sitecore';
import { webStorage } from '@/utils/web-storage';
import { S_LEAD_FORM_SUBMITTED } from '@/utils/web-storage-keys';
import { getGlobalConfigs } from '@/utils/site-utils';

export function validateField(
  value: string,
  validators?: ScItem<FormPatternValidatorFields | FormLengthValidatorFields>[]
) {
  const errors: string[] = [];
  if (!validators || utils.obj.isNoE(value)) return errors;
  for (const val of validators) {
    const validatorFields = val.fields;
    if (!validatorFields) continue;
    let error = undefined;
    if (Object.hasOwn(validatorFields, 'pattern')) {
      error = validateFormPattern(
        value,
        validatorFields as FormPatternValidatorFields,
        val.name || val.displayName
      );
    }
    if (
      Object.hasOwn(validatorFields, 'minLength') &&
      Object.hasOwn(validatorFields, 'maxLength')
    ) {
      error = validateFormLength(value, validatorFields as FormLengthValidatorFields);
    }
    if (error && !utils.obj.isNoE(error)) errors.push(error);
  }
  return errors;
}
const getValidatorFieldValue = <T>(field: { value?: T } | T | undefined, fallback?: T) => {
  if (field && typeof field === 'object' && 'value' in field) {
    return field.value ?? fallback;
  }
  return (field as T | undefined) ?? fallback;
};

const normalizePattern = (pattern: string) =>
  pattern
    .trim()
    .replace(/^\^\s+/, '^')
    .replace(/\s+\$$/, '$')
    .replace(/\\\\/g, '\\');

const testPattern = (value: string, pattern: string, flags: string) => {
  try {
    return new RegExp(pattern, flags).test(value.trim());
  } catch (error) {
    console.warn('Invalid form validator pattern:', pattern, error);
    return false;
  }
};

export function validateFormPattern(
  value: string,
  fields: FormPatternValidatorFields,
  validatorName = ''
) {
  const ignoreCase = getValidatorFieldValue<boolean | string>(fields?.ignoreCase, false);
  const flags = ignoreCase === true || ignoreCase === '1' || ignoreCase === 'true' ? 'i' : '';
  const pattern = getValidatorFieldValue<string>(fields.pattern);
  if (pattern) {
    const valid =
      testPattern(value, pattern, flags) || testPattern(value, normalizePattern(pattern), flags);
    if (!valid) {
      const isEmailValidator = validatorName.toLowerCase().includes('email');
      if (isEmailValidator && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        return undefined;
      }
      return getValidatorFieldValue<string>(fields.message, 'Invalid value');
    }
  }
  return undefined;
}
export function validateFormLength(value: string, fields: FormLengthValidatorFields) {
  const len = value.length;
  if (fields.minLength.value && len < fields.minLength.value) {
    return fields.message.value;
  }
  if (fields.maxLength.value && len > fields.maxLength.value) {
    return fields.message.value;
  }
  return undefined;
}

/**
 * 留资表单提交状态管理
 *
 * 使用场景：用户提交留资表单后，在配置时长内不再弹出表单弹窗
 * 支持多表单类型：不同类型的表单独立管理提交状态
 *
 * 过期时长来自 Sitecore globalConfig 的 leadFormExpireDays 字段（单位：天），
 * 未配置或非法时回退到 S_LEAD_FORM_SUBMITTED.expireMinutes（默认 1 天）。
 */

// 缓存 Sitecore 配置的过期分钟数；默认回退到 S_LEAD_FORM_SUBMITTED.expireMinutes
let leadFormExpireMinutes = S_LEAD_FORM_SUBMITTED.expireMinutes;

/**
 * 初始化留资表单提交状态的过期时间
 * 读取 Sitecore globalConfig 的 leadFormExpireDays（天），未配置或非法时回退到默认值
 * 由 Layout 在 setup 中调用，每次路由切换会刷新缓存
 * @param {Object} page - 页面对象（props.route）
 */
export function initLeadFormConfig(page: unknown): void {
  const days = getGlobalConfigs(page, 'leadFormExpireDays')[0]?.value;
  const parsed = Number(days);
  leadFormExpireMinutes =
    Number.isFinite(parsed) && parsed > 0 ? parsed * 60 * 24 : S_LEAD_FORM_SUBMITTED.expireMinutes;
}

// 构建运行时存储键：复用 S_LEAD_FORM_SUBMITTED.keyData 保持 localStorage 位置稳定，
// 使用 Sitecore 配置的过期分钟数
const getLeadFormKey = () => ({
  keyData: S_LEAD_FORM_SUBMITTED.keyData,
  expireMinutes: leadFormExpireMinutes,
});

/**
 * 标记留资表单已提交
 * 在表单提交成功后调用，记录提交状态到本地存储，过期时长后自动失效
 * @param {string} formType - 表单类型标识（如 'agenda', 'webinar', 'contact' 等）
 */
export function setLeadFormSubmitted(formType: string): void {
  const key = getLeadFormKey();
  const existingData = webStorage.get(key) || {};
  webStorage.set(key, {
    ...existingData,
    [formType]: { submitted: true, timestamp: Date.now() },
  });
}

/**
 * 检查留资表单是否在过期时长内已提交
 * 在弹出表单弹窗前调用，判断是否需要显示弹窗
 * @param {string} formType - 表单类型标识（如 'agenda', 'webinar', 'contact' 等）
 * @returns {boolean} 如果在过期时长内已提交则返回true，否则返回false
 */
export function isLeadFormSubmitted(formType: string): boolean {
  const key = getLeadFormKey();
  const data = webStorage.get(key);
  return !!data?.[formType]?.submitted;
}

/**
 * 清除留资表单提交状态
 * 手动清除已提交状态，使表单可以再次弹出
 * @param {string} formType - 表单类型标识（如 'agenda', 'webinar', 'contact' 等），不传则清除所有类型
 */
export function clearLeadFormSubmitted(formType?: string): void {
  const key = getLeadFormKey();
  if (formType) {
    const existingData = webStorage.get(key) || {};
    delete existingData[formType];
    webStorage.set(key, existingData);
  } else {
    webStorage.clear(key);
  }
}
